import { DEBOUNCE } from '../../constants';
import { DATA_ATTRIBUTES, ID_PREFIXES } from './constants';

// Map to track pending timeouts for elements to prevent memory leaks
const elementTimeouts = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Adds data-action-id attributes to first-level children of the actions container
 * Ensures unique IDs by checking existing ones
 */
export function addActionIdsToFirstLevelChildren(actionsContainerEl: HTMLElement | null): void {
  if (!actionsContainerEl) {
    return;
  }

  // Get all direct children of the actions container
  const directChildren = Array.from(actionsContainerEl.children) as HTMLElement[];

  // Collect existing action IDs to avoid duplicates
  const existingIds = new Set<string>();
  directChildren.forEach((child) => {
    const existingId = child.getAttribute(DATA_ATTRIBUTES.ACTION_ID);
    if (existingId) {
      existingIds.add(existingId);
    }
  });

  // Find the next available index for auto-generated IDs
  let nextIndex = 0;
  while (existingIds.has(`auto-action-${nextIndex}`)) {
    nextIndex++;
  }

  directChildren.forEach((child) => {
    // Only add data-action-id if it doesn't already exist
    if (!child.hasAttribute(DATA_ATTRIBUTES.ACTION_ID)) {
      child.setAttribute(DATA_ATTRIBUTES.ACTION_ID, `${ID_PREFIXES.AUTO_ACTION}${nextIndex}`);
      nextIndex++;
    }
  });
}

/**
 * Creates a map of elements to their IDs
 */
export function createElementIdMap(directChildren: HTMLElement[]): Map<HTMLElement, string> {
  const elementIdMap = new Map<HTMLElement, string>();

  directChildren.forEach((child, index) => {
    const actionId = child.getAttribute(DATA_ATTRIBUTES.ACTION_ID);
    if (actionId) {
      elementIdMap.set(child, actionId);
    } else {
      // Use index as ID for elements without data-action-id
      elementIdMap.set(child, `${ID_PREFIXES.INDEX_BASED}${index}`);
    }
  });

  return elementIdMap;
}

/**
 * Cleans up overflow IDs for elements that no longer exist
 */
export function cleanupOverflowIds(overflowIds: Set<string>, currentElementIds: Set<string>): void {
  const idsToRemove: string[] = [];

  overflowIds.forEach((id) => {
    if (!currentElementIds.has(id)) {
      idsToRemove.push(id);
    }
  });

  idsToRemove.forEach((id) => {
    overflowIds.delete(id);
  });
}

/**
 * Applies visibility classes to elements based on overflow state
 */
export function applyElementVisibility(element: HTMLElement, elementId: string, overflowIds: Set<string>): void {
  // Clear any pending timeout for this element
  const existingTimeout = elementTimeouts.get(element);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    elementTimeouts.delete(element);
  }

  if (overflowIds.has(elementId)) {
    element.removeAttribute(DATA_ATTRIBUTES.PROCESSED);
    element.classList.add('invisible');
  } else {
    const timeout = setTimeout(() => {
      if (element.isConnected) {
        element.setAttribute(DATA_ATTRIBUTES.PROCESSED, 'true');
      }
      elementTimeouts.delete(element);
    }, DEBOUNCE.FAST);
    elementTimeouts.set(element, timeout);
    element.classList.remove('invisible');
  }
}

/**
 * Syncs more-actions content visibility based on overflow state
 * Shows more-actions items whose data-action-id is in overflowIds
 * Supports nested elements with data-action-id (e.g., Menu > MenuItem)
 */
export function syncMoreActionsVisibility(moreDropdownMenuEl: HTMLElement | null, overflowIds: Set<string>): void {
  if (!moreDropdownMenuEl) {
    return;
  }

  // Find all elements with data-action-id at any nesting level (for nested wrappers like Menu > MenuItem)
  const nestedItemsWithActionId = Array.from(
    moreDropdownMenuEl.querySelectorAll(`[${DATA_ATTRIBUTES.ACTION_ID}]`),
  ) as HTMLElement[];

  // Filter to only "leaf" elements - those that don't contain other elements with data-action-id
  // This prevents hiding wrapper elements that contain the actual actionable items
  const leafItems = nestedItemsWithActionId.filter((element) => {
    const nestedActionsInside = element.querySelectorAll(`[${DATA_ATTRIBUTES.ACTION_ID}]`);
    // If element has nested elements with data-action-id inside, it's a wrapper, not a leaf
    return nestedActionsInside.length === 0;
  });

  // Apply styles and visibility to leaf items only
  leafItems.forEach((element) => {
    const actionId = element.getAttribute(DATA_ATTRIBUTES.ACTION_ID);

    // Show more-actions item only if its action-id is in overflowIds (item is hidden in toolbar)
    const shouldShow = actionId && overflowIds.has(actionId);
    if (shouldShow) {
      element.classList.remove('hidden');
    } else {
      element.classList.add('hidden');
    }
  });
}
